import produce from 'immer';
import * as _ from 'lodash';

import { createReducer } from '../utils';
import { actionTypes } from './actions';
import type { DependenciesV2Store, HydrateQueryState } from './types';
import type { AnnotatedLicense, FetchTimestamp, ReduxAction, ReduxActionResponse, SyncReduxAction } from '../../types';
import type { DependenciesResponse, Dependency, DependencyPackageManagersResponse, DependencyRootProjectsResponse, DependencyScope, RevisionWithAssociations } from '../../../shared/dependenciesV2';
import * as asyncValue from '../asyncValue';
import * as asyncResponse from '../asyncResponse';
import { errorMessagesListFromResponse } from '../../helpers/errors';
import { getIsAnalyzing } from '../../components/projects/dependenciesV2/DependenciesTable/DependencyTableRow/helpers';
import initialState from './initialState';

/**
 * Update dependency data from a revision instance
 * @param {RevisionInstance} revision to pull data from
 * @returns {Partial<Dependency>} Partial updated dependency data
 */
const updateDependencyFromRevision = (revision: RevisionWithAssociations): Partial<Dependency> => ({
  title: revision?.project?.title,
  licenses: revision?.licenses?.map(license => license.licenseId),
  status: {
    analyzing: getIsAnalyzing(revision?.builds ?? []) ?? false,
    error: revision.error || '',
    resolved: revision.resolved ?? false,
    unsupported: revision.unsupported ?? false,
  },
});

// Clears build watch interval and revisions to watch
const clearBuildWatch = (draftState: DependenciesV2Store) => {
  if (draftState.buildWatch.interval) {
    clearTimeout(draftState.buildWatch.interval);
    draftState.buildWatch.interval = undefined;
  }

  draftState.buildWatch.revisionsToWatch = [];
};

// Pagination resets after every request.
const resetPage = (draftState: DependenciesV2Store) => {
  draftState.page = initialState.page;
  clearBuildWatch(draftState);
};

const updateDependency = (
  state: DependenciesV2Store,
  draftState: DependenciesV2Store,
  locator: string,
  updatedData: Partial<Dependency>,
) => {
  const dependencyIndex = state.dependencies.value?.response?.findIndex(
    dependency => dependency.locator === locator,
  );
  if (_.isNil(dependencyIndex) || dependencyIndex === -1) {
    return;
  }

  const dependency = state.dependencies.value?.response?.[dependencyIndex];
  if (!dependency) {
    return;
  }

  const changedDependency = {
    ...dependency,
    ...updatedData,
  };

  draftState.dependencies.value?.response?.splice(dependencyIndex, 1, changedDependency);
};

const resetReducer = {
  [actionTypes.RESET]: (state: DependenciesV2Store) => {
    // Clear timeout on reset
    clearTimeout(state.buildWatch.interval);
    return initialState;
  },
};

const preFetchLoadingReducer = {
  [actionTypes.PRE_FETCH_LOADING]: (state: DependenciesV2Store) =>
    produce(state, draftState => {
      draftState.dependencies = asyncValue.setPending(draftState.dependencies);
    }),
};

const scopeReducer = {
  [actionTypes.SET_SCOPE]: (state: DependenciesV2Store, action: SyncReduxAction<{ scope: DependencyScope }>) =>
    produce(state, draftState => {
      draftState.scope = action.scope;
    }),
};

const paginationReducer = {
  [actionTypes.SET_PAGE]: (state: DependenciesV2Store, action: SyncReduxAction<{ page: number }>) =>
    produce(state, draftState => {
      draftState.page = action.page;
    }),
  [actionTypes.SET_COUNT]: (state: DependenciesV2Store, action: SyncReduxAction<{ count: number }>) =>
    produce(state, draftState => {
      draftState.count = action.count;
      // Reset the page to 1 when the count changes - otherwise the selected page may not exist
      resetPage(draftState);
    }),
};

const filterReducer = {
  [actionTypes.SET_FILTERS]: (state: DependenciesV2Store, action: SyncReduxAction<{ filters: HydrateQueryState }>) =>
    produce(state, draftState => {
      draftState.filters = action.filters;
      // Reset the page to 1 when the count changes - otherwise the selected page may not exist
      resetPage(draftState);
    }),
};

const isOrgMemberReducer = {
  [actionTypes.SET_IS_ORG_MEMBER]: (state: DependenciesV2Store, action: SyncReduxAction<{ isOrgMember: boolean }>) =>
    produce(state, draftState => {
      draftState.isOrgMember = action.isOrgMember;
    }),
};

const hydrateStateReducer = {
  [actionTypes.HYDRATE_STATE]: (
    state: DependenciesV2Store, action: SyncReduxAction<{ query: HydrateQueryState }>,
  ) =>
    produce(state, draftState => {
      // omit scope-related hydration fields here because they aren't filters
      // and have already been processed (to determine scope)
      const { page, count, ...filters } = _.omit(action.query, 'revision', 'projectGroupId', 'projectGroupReleaseId');
      draftState.page = page ?? initialState.page;
      draftState.count = count ?? initialState.count;
      draftState.filters = { ...state.filters, ...filters };
    }),
};

const fetchDependenciesReducer = {
  [`${actionTypes.FETCH_DEPENDENCIES}_PENDING`]: (state: DependenciesV2Store, action: ReduxActionResponse<DependenciesResponse, FetchTimestamp>) =>
    produce(state, draftState => {
      const timestamp = action?.meta?.timestamp || 0;
      draftState.dependencies = asyncResponse.pending({ timestamp });
    }),
  [`${actionTypes.FETCH_DEPENDENCIES}_FULFILLED`]: (state: DependenciesV2Store, action: ReduxActionResponse<DependenciesResponse, FetchTimestamp>) =>
    produce(state, draftState => {
      if (draftState.dependencies.value?.meta?.timestamp === action?.meta?.timestamp) {
        draftState.dependencies = asyncResponse.resolve(action.payload?.data?.dependencies ?? []);
        draftState.totalDependencies = action.payload?.data?.total ?? 0;
      }
    }),
  [`${actionTypes.FETCH_DEPENDENCIES}_REJECTED`]: (state: DependenciesV2Store, action: ReduxAction) =>
    produce(state, draftState => {
      draftState.dependencies = asyncResponse.reject(
        errorMessagesListFromResponse(action.payload),
      );
    }),
};

const updateDependencyReducer = {
  [actionTypes.UPDATE_DEPENDENCY]: (
    state: DependenciesV2Store,
    action: SyncReduxAction<{ locator: string, dependency: Partial<Dependency> }>,
  ) =>
    produce(state, draftState => updateDependency(state, draftState, action.locator, action.dependency)),
};

const resolveDependencyReducer = {
  [`${actionTypes.RESOLVE_DEPENDENCY}_PENDING`]: (state: DependenciesV2Store) =>
    produce(state, draftState => {
      draftState.resolveDependency = asyncValue.pending();
    }),
  [`${actionTypes.RESOLVE_DEPENDENCY}_FULFILLED`]: (state: DependenciesV2Store, action: ReduxActionResponse<null>) =>
    produce(state, draftState => {
      draftState.resolveDependency = asyncValue.resolve(action.payload?.data ?? null);
    }),
  [`${actionTypes.RESOLVE_DEPENDENCY}_REJECTED`]: (state: DependenciesV2Store, action: ReduxAction) =>
    produce(state, draftState => {
      draftState.resolveDependency = asyncValue.reject(
        errorMessagesListFromResponse(action.payload),
      );
    }),
};

const fetchLicensesReducer = {
  [`${actionTypes.FETCH_LICENSES}_PENDING`]: (state: DependenciesV2Store) =>
    produce(state, draftState => {
      draftState.licenses = asyncValue.pending();
    }),
  [`${actionTypes.FETCH_LICENSES}_FULFILLED`]: (state: DependenciesV2Store, action: ReduxActionResponse<AnnotatedLicense[]>) =>
    produce(state, draftState => {
      // Project licenses come as an array (AnnotatedLicense[]) but release group licenses
      // come as an object ({ [string]: AnnotatedLicense }). Using Object.values here
      // converts both cases into the desired shape (an array of AnnotatedLicences).
      draftState.licenses = asyncValue.resolve(Object.values(action.payload?.data ?? []));
    }),
  [`${actionTypes.FETCH_LICENSES}_REJECTED`]: (state: DependenciesV2Store, action: ReduxAction) =>
    produce(state, draftState => {
      draftState.licenses = asyncValue.reject(
        errorMessagesListFromResponse(action.payload),
      );
    }),
};

const fetchPackageManagersReducer = {
  [`${actionTypes.FETCH_PACKAGE_MANAGERS}_PENDING`]: (state: DependenciesV2Store) =>
    produce(state, draftState => {
      draftState.packageManagers = asyncValue.pending();
    }),
  [`${actionTypes.FETCH_PACKAGE_MANAGERS}_FULFILLED`]: (state: DependenciesV2Store, action: ReduxActionResponse<DependencyPackageManagersResponse>) =>
    produce(state, draftState => {
      draftState.packageManagers = asyncValue.resolve(action.payload?.data?.packageManagers ?? []);
    }),
  [`${actionTypes.FETCH_PACKAGE_MANAGERS}_REJECTED`]: (state: DependenciesV2Store, action: ReduxAction) =>
    produce(state, draftState => {
      draftState.licenses = asyncValue.reject(
        errorMessagesListFromResponse(action.payload),
      );
    }),
};

const fetchRootProjectsReducer = {
  [`${actionTypes.FETCH_ROOT_PROJECTS}_PENDING`]: (state: DependenciesV2Store) =>
    produce(state, draftState => {
      draftState.rootProjects = asyncValue.pending();
    }),
  [`${actionTypes.FETCH_ROOT_PROJECTS}_FULFILLED`]: (state: DependenciesV2Store, action: ReduxActionResponse<DependencyRootProjectsResponse>) =>
    produce(state, draftState => {
      draftState.rootProjects = asyncValue.resolve(action.payload?.data?.rootProjects ?? []);
    }),
  [`${actionTypes.FETCH_ROOT_PROJECTS}_REJECTED`]: (state: DependenciesV2Store, action: ReduxAction) =>
    produce(state, draftState => {
      draftState.rootProjects = asyncValue.reject(
        errorMessagesListFromResponse(action.payload),
      );
    }),
};

const buildWatchReducer = {
  [actionTypes.BUILD_WATCH_ADD_REVISION]: (state: DependenciesV2Store, action: SyncReduxAction<{ revision: string }>) =>
    produce(state, draftState => {
      const index = draftState.buildWatch.revisionsToWatch.indexOf(action.revision);
      if (index === -1) {
        draftState.buildWatch.revisionsToWatch.push(action.revision);
      }
    }),
  [actionTypes.BUILD_WATCH_REMOVE_REVISION]: (
    state: DependenciesV2Store, action: SyncReduxAction<{ revision: string }>,
  ) =>
    produce(state, draftState => {
      const index = draftState.buildWatch.revisionsToWatch.indexOf(action.revision);
      if (index > -1) {
        draftState.buildWatch.revisionsToWatch.splice(index, 1);
      }
    }),
  [actionTypes.BUILD_WATCH_UPDATE_INTERVAL]: (
    state: DependenciesV2Store,
    // eslint-disable-next-line no-undef
    action: SyncReduxAction<{ interval: NodeJS.Timeout }>,
  ) =>
    produce(state, draftState => {
      draftState.buildWatch.interval = action.interval;
    }),
  [actionTypes.BUILD_WATCH_STOP]: (state: DependenciesV2Store) =>
    produce(state, draftState => {
      if (draftState.buildWatch.interval) {
        clearTimeout(draftState.buildWatch.interval);
        draftState.buildWatch.interval = undefined;
      }

      draftState.buildWatch.revisionsToWatch = [];
    }),
  [`${actionTypes.BUILD_WATCH_FETCH_REVISIONS}_PENDING`]: (state: DependenciesV2Store) =>
    produce(state, draftState => {
      draftState.buildWatch.revisions = asyncValue.setPending(draftState.buildWatch.revisions);
    }),
  [`${actionTypes.BUILD_WATCH_FETCH_REVISIONS}_FULFILLED`]: (state: DependenciesV2Store, action: ReduxActionResponse<RevisionWithAssociations[]>) =>
    produce(state, draftState => {
      const payloadData = action.payload?.data ?? [];
      const revisionData = draftState.buildWatch.revisions.value ?? {};

      // If there are no revisions in the response, clear the build watch
      if (payloadData.length === 0) {
        clearBuildWatch(draftState);
      }

      // Organize data by locator
      // this allow us to retain data even when a revision is removed from the watch list
      payloadData.forEach((revision) => {
        revisionData[revision.locator] = revision;

        updateDependency(state, draftState, revision.locator, updateDependencyFromRevision(revision));
      });

      draftState.buildWatch.revisions = asyncValue.resolve(revisionData);
    }),
  [`${actionTypes.BUILD_WATCH_FETCH_REVISIONS}_REJECTED`]: (state: DependenciesV2Store, action: ReduxAction) =>
    produce(state, draftState => {
      draftState.buildWatch.revisions = asyncValue.reject(
        errorMessagesListFromResponse(action.payload),
      );
    }),
};

export default createReducer<DependenciesV2Store>(initialState, {
  ...resetReducer,
  ...preFetchLoadingReducer,
  ...scopeReducer,
  ...paginationReducer,
  ...filterReducer,
  ...isOrgMemberReducer,
  ...hydrateStateReducer,
  ...fetchDependenciesReducer,
  ...updateDependencyReducer,
  ...resolveDependencyReducer,
  ...fetchLicensesReducer,
  ...fetchPackageManagersReducer,
  ...fetchRootProjectsReducer,
  ...buildWatchReducer,
});
